import { CountryCode } from '~shared/constants/country-code.enum';
import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Product } from '~shared/models';
import { select, Store } from '@ngrx/store';
import * as fromCatalog from '../../../../../../modules/catalog/store';
import * as fromSearch from '../../../../../../modules/generic/store/search';
import { DEFAULT_HUB_LIMIT, DEFAULT_LIMIT } from '~app/modules/generic/constants';
import { NavigationHelper, NavigationHelperConfig } from '~shared/services/navigation-helper.service';
import { ProductsLoadingService } from '~app/modules/catalog/services/products-loading.service';
import { Params, Router } from '@angular/router';
import { FilterOptionsUtil } from '~app/modules/catalog/utils';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import {
  ElementVisibilityInViewportApplicationEvent,
  PLPPaginationShowMoreClickEvent,
  SearchApplicationEvent,
} from '~shared/modules/application-event/models';
import { GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { RecommendedProductsType } from '~app/modules/catalog/models';
import { NUMBER_1 } from '~shared/constants';
import { ApplicationSearchEventSource } from '~shared/modules/application-event/constants';
import { ResetNavigateToSearchRoute } from '~app/modules/generic/store';
import * as fromGlobalCart from '~shared/modules/shopping-cart-global/store';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Pagination } from '~app/modules/catalog/models/pagination.model';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services';
import { Category } from '~app/modules/generic/models';
import { CategoryPageViewedApplicationEvent } from '~shared/modules/application-event/models/category-page-viewed-application-event.model';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { VATDisclaimerService } from '~shared/services/vat-disclaimer.service';

@Component({
  selector: 'app-catalog-body',
  templateUrl: './catalog-body.component.html',
  styleUrls: ['./catalog-body.component.scss'],
  providers: [ProductsLoadingService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogBodyComponent implements OnInit, OnDestroy {
  @Input() searchQuery: string;
  @Input() totalCount: number;
  @Input() queryParams: Params;
  @Input() isZeroResult: boolean;
  @Input() isDesktop: boolean;
  @Input() isMobileOrTablet: boolean;
  @Input() isCustomPLPPage: boolean;
  @Input() products: Product[];
  @Input() recommendedProducts: Product[];
  @Input() recommendedProductsType: RecommendedProductsType;
  @Input() searchCorrection: string;
  @Input() isExpired: boolean;
  @Input() hasFilters: boolean;
  @Input() isCategoryPage: boolean;
  @Input() selectedCategory: Category;
  @Input() isMobile: boolean;
  @Input() isTablet: boolean;

  isProductsLoading$: Observable<boolean>;
  isCartQuantityZero$: Observable<boolean>;
  defaultLimit: number = DEFAULT_LIMIT;
  recommendedProductsLimit = DEFAULT_HUB_LIMIT;
  isSearchFromHeaderBar$: Observable<boolean>;
  pageNumberWhenUserSearchedFromHeaderBar$: Observable<number>;
  currentCountryCode: CountryCode;
  spainCountryCode = CountryCode.Spain;
  isVATDisclaimerEnabled: boolean;

  private navigatedToSearchSubscription: Subscription;
  isLoggedIn$ = this.identityService.isLoggedIn();
  isB2CUser$: Observable<boolean> = this.priceTypeService.priceType$.pipe(
    map((priceType: PriceTypes) => priceType === PriceTypes.b2c)
  );

  constructor(
    private router: Router,
    private catalogStateStore: Store<fromCatalog.CatalogState>,
    private searchStateStore: Store<fromSearch.SearchState>,
    private navigationHelper: NavigationHelper,
    private productsLoadingService: ProductsLoadingService,
    private applicationEventService: ApplicationEventService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
    private identityService: IdentityService,
    private priceTypeService: PriceTypeService,
    private productsListUrlService: ProductsListUrlService,
    private readonly vatDisclaimerService: VATDisclaimerService
  ) {
    this.isVATDisclaimerEnabled = this.vatDisclaimerService.getVATDisclaimerForCountry(
      getCountryDomainCode(this.webAppBuyerRequest)
    );
  }

  ngOnInit(): void {
    this.isProductsLoading$ = this.productsLoadingService.isProductsLoading$;
    this.currentCountryCode = getCountryDomainCode(this.webAppBuyerRequest);
    this.isSearchFromHeaderBar$ = this.searchStateStore.pipe(select(fromSearch.isSearchFromHeaderBar));
    this.pageNumberWhenUserSearchedFromHeaderBar$ = this.searchStateStore.pipe(
      select(fromSearch.getPageNumberWhenUserSearchedFromHeaderBar)
    );
    if (this.isCategoryPage) {
      this.applicationEventService.publish(
        new CategoryPageViewedApplicationEvent(
          this.productsListUrlService.getCategory(),
          ApplicationSearchEventSource.Direct,
          this.hasFilters,
          this.isExpired,
          this.totalCount
        )
      );
    }

    this.navigatedToSearchSubscription = this.searchStateStore
      .pipe(select(fromSearch.getNavigatedToSearch), take(1))
      .subscribe((didUserNavigatedToSearch: boolean) => {
        if (didUserNavigatedToSearch) {
          this.fireSearchEventWhenNavigatedToSearchPage();
        }
      });

    this.isCartQuantityZero$ = this.catalogStateStore.pipe(select(fromGlobalCart.getCartTotalQuantity)).pipe(
      map((quantity: string | number) => {
        return !quantity || quantity === 0;
      })
    );
  }

  ngOnDestroy(): void {
    this.navigatedToSearchSubscription.unsubscribe();
  }

  onPageSelected(pagination: Pagination): void {
    this.applicationEventService.publish(new PLPPaginationShowMoreClickEvent(pagination, DEFAULT_LIMIT));

    const { limit, offset, ...rest } = this.queryParams || {};

    if (rest?.filter?.options) {
      rest.filter = {
        ...rest.filter,
        options: FilterOptionsUtil.formatFilterOptions(rest.filter.options),
      };
    }

    if (rest?.filter?.categories) {
      rest.filter = {
        ...rest.filter,
        categories: FilterOptionsUtil.formatFilterOptions({ options: rest.filter.categories }).options,
      };
    }

    if (rest?.filter?.categoryIds) {
      rest.filter = {
        ...rest.filter,
        categoryIds: FilterOptionsUtil.formatFilterOptions({ options: rest.filter.categoryIds }).options,
      };
    }

    this.updateRouterState({ limit: pagination.limit, offset: pagination.offset, ...rest });
  }

  onPaginationVisibilityChange(isVisible: boolean): void {
    // TODO Talk with storefront guys and change the way this scrolling event is working
    this.fireElementVisibility(`Pagination ${isVisible ? 'is' : 'is not'}`);
  }

  onProductVisibility(productPosition: number): void {
    this.fireElementVisibility(`Product ${productPosition} is`);
  }

  switchOffManualSearch(): void {
    this.searchStateStore.dispatch(
      new fromSearch.SearchFromHeaderBar({
        isSearchFromHeaderBar: false,
        pageNumberWhenUserSearchedFromHeaderBar: NUMBER_1,
      })
    );
  }

  private fireSearchEventWhenNavigatedToSearchPage(): void {
    this.catalogStateStore.dispatch(new ResetNavigateToSearchRoute());
    this.applicationEventService.publish(
      new SearchApplicationEvent(
        this.searchQuery,
        ApplicationSearchEventSource.Direct,
        this.searchCorrection,
        false,
        false,
        NUMBER_1,
        this.totalCount
      )
    );
  }

  private getCurrentPath(): string {
    return this.router.url.split('?')[0].slice(1);
  }

  private updateRouterState(params?: Params, config?: NavigationHelperConfig): void {
    const path = this.getCurrentPath();
    this.navigationHelper.replaceUrl([path], { queryParams: params }, config);
  }

  private fireElementVisibility(label: string): void {
    this.applicationEventService.publish(
      new ElementVisibilityInViewportApplicationEvent(GATrackingEventType.PLPScrolling, label)
    );
  }
}
